import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { ResilientImg } from './ResilientImg';

const GalleryBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(7, 10, 18, 0.88);
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 56px;
  animation: galleryFadeIn 0.25s ease-out;
  @keyframes galleryFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const GalleryCloseBtn = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #e9ecf1;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, transform 0.15s ease;
  z-index: 2;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const GalleryImageWrap = styled.div`
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  padding: 20px ${(p) => (p.$hasFocused ? 'calc(50vw - 120px)' : '48px')};
  overflow-x: auto;
  overflow-y: visible;
  scroll-snap-type: ${(p) => (p.$hasFocused ? 'none' : 'x mandatory')};
  scroll-padding-inline: 48px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  transition: padding 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  &::-webkit-scrollbar {
    display: none;
  }
`;

const GalleryImageItem = styled.div`
  flex-shrink: 0;
  scroll-snap-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    opacity 0.35s ease,
    margin 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  transform: ${(p) => {
    const rot = p.$rotate ?? 0;
    if (p.$focused) return 'rotate(0deg) scale(1.35)';
    if (p.$hasSiblingFocused) return `rotate(${rot}deg) scale(0.7)`;
    return `rotate(${rot}deg) scale(1)`;
  }};

  opacity: ${(p) => {
    if (p.$focused) return 1;
    if (p.$hasSiblingFocused) return 0.35;
    return 1;
  }};

  margin: ${(p) => {
    if (!p.$hasSiblingFocused) return '0';
    if (p.$offsetFromFocused < 0) return '0 0 0 -20px';
    if (p.$offsetFromFocused > 0) return '0 -20px 0 0';
    return '0';
  }};

  z-index: ${(p) => (p.$focused ? 10 : 1)};
  filter: ${(p) => (p.$hasSiblingFocused && !p.$focused ? 'blur(1.5px)' : 'none')};
`;

const GalleryImage = styled.img`
  width: auto;
  max-width: min(420px, 45vw);
  max-height: 75vh;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: ${(p) =>
    p.$focused
      ? '0 12px 48px rgba(0, 0, 0, 0.55), 0 0 0 2px rgba(255, 255, 255, 0.15)'
      : '0 8px 32px rgba(0, 0, 0, 0.35)'};
  transition: box-shadow 0.35s ease, max-width 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94), max-height 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  ${(p) => p.$focused && `
    max-width: min(680px, 80vw);
    max-height: 82vh;
  `}
`;

const GalleryDots = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const GalleryDot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  padding: 0;
  background: ${(p) => (p.$active ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.35)')};
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease;

  &:hover {
    transform: scale(1.2);
    background: rgba(255, 255, 255, 0.9);
  }
`;

const ROTATIONS = [-2, 1.5, -1.2, 2.3, 0.8, -1.8, 1.2, -0.5];

export function GalleryModal({
  photos,
  galleryIndex,
  setGalleryIndex,
  galleryIndexRef,
  scrollGalleryToIndex,
  galleryScrollRef,
  onClose,
  entryId,
  getPhotoRotation,
}) {
  const [focusedIndex, setFocusedIndex] = useState(null);
  const itemRefs = React.useRef([]);

  const scrollToCenter = useCallback((index) => {
    const el = galleryScrollRef?.current;
    const item = itemRefs.current[index];
    if (!el || !item) return;
    const itemCenter = item.offsetLeft + item.offsetWidth / 2;
    const scrollLeft = itemCenter - el.offsetWidth / 2;
    el.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
  }, [galleryScrollRef]);

  const handleImageClick = useCallback((e, i) => {
    e.stopPropagation();
    setFocusedIndex((prev) => {
      const next = prev === i ? null : i;
      if (next !== null) {
        requestAnimationFrame(() => scrollToCenter(next));
        setTimeout(() => scrollToCenter(next), 480);
      }
      return next;
    });
  }, [scrollToCenter]);

  const handleBackdropClick = useCallback((e) => {
    if (focusedIndex !== null) {
      setFocusedIndex(null);
      e.stopPropagation();
      return;
    }
    onClose();
  }, [focusedIndex, onClose]);

  if (!photos || photos.length === 0) return null;

  const hasFocused = focusedIndex !== null;

  return (
    <GalleryBackdrop onClick={handleBackdropClick} role="dialog" aria-modal="true" aria-label="照片 gallery">
      <GalleryCloseBtn type="button" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="關閉">×</GalleryCloseBtn>
      <GalleryImageWrap ref={galleryScrollRef} $hasFocused={hasFocused} onClick={(e) => e.stopPropagation()}>
        {photos.map((photo, i) => {
          const src = photo && typeof photo === 'object' ? photo.src : photo;
          const rotEntryId = photo && typeof photo === 'object' ? photo.entryId : entryId;
          return (
          <GalleryImageItem
            key={`${String(src)}-${i}`}
            ref={(el) => { itemRefs.current[i] = el; }}
            $rotate={ROTATIONS[i % 8]}
            $focused={focusedIndex === i}
            $hasSiblingFocused={hasFocused && focusedIndex !== i}
            $offsetFromFocused={hasFocused ? i - focusedIndex : 0}
            onClick={(e) => handleImageClick(e, i)}
          >
            <GalleryImage
              as={ResilientImg}
              src={src}
              alt=""
              $focused={focusedIndex === i}
              loading="eager"
              rotateDeg={
                typeof getPhotoRotation === 'function'
                  ? getPhotoRotation(rotEntryId, i, src)
                  : undefined
              }
            />
          </GalleryImageItem>
          );
        })}
      </GalleryImageWrap>
      {photos.length > 1 && (
        <GalleryDots onClick={(e) => e.stopPropagation()}>
          {photos.map((_, i) => (
            <GalleryDot
              key={i}
              type="button"
              $active={i === galleryIndex}
              aria-label={`第 ${i + 1} 張`}
              aria-current={i === galleryIndex ? 'true' : undefined}
              onClick={() => {
                setGalleryIndex(i);
                if (galleryIndexRef) galleryIndexRef.current = i;
                scrollGalleryToIndex(i);
                setFocusedIndex(null);
              }}
            />
          ))}
        </GalleryDots>
      )}
    </GalleryBackdrop>
  );
}
